import {IM_CODES} from "../../constants/imcodes";
import WORD_USAGE from "../../constants/word-usage.json";

const PARTIAL_MATCH_MIN_LEN = 1;
const IMAGE_MATCH_MAX = 14;
const PARTIAL_MATCH_MAX_IMAGES = IMAGE_MATCH_MAX;
const PARTIAL_MATCH_PER_KEY_MAX = 2;
const ARTICLE_EXACT_PER_KEY_MAX = 2;
const SIMILAR_TEXT_MAX = 24;
//longest first so "an " is matched before "a "
const DETERMINERS = ["some ", "the ", "an ", "a "];

const IRREGULAR_SINGULAR_TO_PLURAL:Record<string, string>={
	mouse:"mice",
	child:"children",
	person:"people",
	foot:"feet",
	tooth:"teeth",
	man:"men",
	woman:"women",
	goose:"geese",
	fish:"fish",
	sheep:"sheep",
	leaf:"leaves",
	loaf:"loaves",
	knife:"knives",
	wife:"wives",
	life:"lives",
	half:"halves",
	shelf:"shelves",
	wolf:"wolves",
	cactus:"cacti",
	die:"dice",
};

export default class Searcher{

	private items:string[];
	private itemsWithCaps:string[];
	private usageByItem:Record<string, number>;
	private irregularByWord:Record<string, string[]>;

    constructor(){
		this.itemsWithCaps=Object.keys(IM_CODES);
		this.items=[];
		for(let i=0;i<this.itemsWithCaps.length;i++){
			this.items[i]=this.itemsWithCaps[i].toLowerCase();
		}
		this.usageByItem={};
		for(const [word, count] of Object.entries(WORD_USAGE)){
			const wordLower=word.toLowerCase();
			this.usageByItem[wordLower]=(this.usageByItem[wordLower]||0)+Number(count);
		}
		//build a bidirectional lookup so either form points at its counterpart
		this.irregularByWord={};
		for(const [singular, plural] of Object.entries(IRREGULAR_SINGULAR_TO_PLURAL)){
			if(singular===plural)continue;
			(this.irregularByWord[singular]=this.irregularByWord[singular]||[]).push(plural);
			(this.irregularByWord[plural]=this.irregularByWord[plural]||[]).push(singular);
		}
    }

	getTextSuggestions(searchText:string):string[]{
		if(searchText.length===0){
			return [];
		}
		const noun=this.nounAfterDeterminer(searchText);
		//no determiner, or determiner with nothing after it: suggest based on the whole input
		if(noun===null){
			return this.baseSuggestions(searchText);
		}
		//determiner + noun (e.g. "some apple"): show phrases that extend the full input first
		//(e.g. "some apples", "some apple juice"), then fall back to suggestions for the noun
		//itself rather than backfilling with other phrases that merely share the determiner
		const fullPhraseMatches=this.findSimilarText(searchText).filter((item)=>item.startsWith(searchText));
		const nounSuggestions=this.baseSuggestions(noun);
		return this.dedupe([...fullPhraseMatches, ...nounSuggestions]);
	}

	private baseSuggestions(searchText:string):string[]{
		const numberVariations=this.getNumberVariations(searchText);
		const articleVariations=this.getArticleVariations(searchText);
		const similarText=this.findSimilarText(searchText);
		return this.dedupe([...numberVariations, ...articleVariations, ...similarText]);
	}

	private nounAfterDeterminer(searchText:string):string|null{
		for(const determiner of DETERMINERS){
			if(searchText.startsWith(determiner)){
				const noun=searchText.slice(determiner.length).trim();
				return noun.length>0?noun:null;
			}
		}
		return null;
	}

	private dedupe(items:string[]):string[]{
		const seen=new Set<string>();
		const out:string[]=[];
		for(const item of items){
			if(seen.has(item))continue;
			seen.add(item);
			out.push(item);
		}
		return out;
	}

	getNumberVariations(searchText:string):string[]{
		if(searchText.length===0){
			return [];
		}
		const out:string[]=[];
		const seen=new Set<string>();
		const tryKey=(key:string)=>{
			if(key===searchText)return;
			if(seen.has(key))return;
			if(IM_CODES[key]!==undefined){
				out.push(key);
				seen.add(key);
			}
		};
		//irregulars first so mouse<->mice, child<->children etc. are reliably covered
		const irregulars=this.irregularByWord[searchText];
		if(irregulars!==undefined){
			for(const form of irregulars){
				tryKey(form);
			}
		}
		//we don't know if the input is singular or plural, so try both directions and keep whatever's real
		for(const form of this.pluralCandidates(searchText)){
			tryKey(form);
		}
		for(const form of this.singularCandidates(searchText)){
			tryKey(form);
		}
		return out;
	}

	private singularCandidates(word:string):string[]{
		const forms:string[]=[];
		if(word.length>3&&word.endsWith("ies")){
			forms.push(word.slice(0,-3)+"y");
		}
		if(word.length>2&&word.endsWith("es")){
			forms.push(word.slice(0,-2));
		}
		if(word.length>1&&word.endsWith("s")){
			forms.push(word.slice(0,-1));
		}
		return forms;
	}

	getArticleVariations(searchText:string):string[]{
		if(searchText.length===0){
			return [];
		}
		const out:string[]=[];
		const seen=new Set<string>();
		const tryKey=(key:string)=>{
			if(key===searchText)return;
			if(seen.has(key))return;
			if(IM_CODES[key]!==undefined){
				out.push(key);
				seen.add(key);
			}
		};
		//a/an/the just glue onto the typed word; the data only holds the grammatical form
		tryKey("a "+searchText);
		tryKey("an "+searchText);
		tryKey("the "+searchText);
		//some usually pairs with the plural, so try a few plural spellings of the typed word
		for(const plural of this.pluralCandidates(searchText)){
			tryKey("some "+plural);
		}
		return out;
	}

	private pluralCandidates(word:string):string[]{
		const forms=[word,word+"s"];
		if(word.endsWith("s")||word.endsWith("x")||word.endsWith("z")||word.endsWith("ch")||word.endsWith("sh")){
			forms.push(word+"es");
		}
		if(word.length>1&&word.endsWith("y")&&!this.isVowel(word[word.length-2])){
			forms.push(word.slice(0,-1)+"ies");
		}
		return forms;
	}

	private isVowel(ch:string):boolean{
		return ch==="a"||ch==="e"||ch==="i"||ch==="o"||ch==="u";
	}

    findImages(searchText:string){
		const result:string[][]=[];
		const seenCodes=new Set<string>();

		//1. exact matches for the typed word
		const exactRes=this.findImagesExact(searchText);
		for(let i=0;i<exactRes.length&&result.length<IMAGE_MATCH_MAX;i++){
			const code=exactRes[i][1];
			if(seenCodes.has(code))continue;
			seenCodes.add(code);
			result.push(exactRes[i]);
		}
		if(result.length>=IMAGE_MATCH_MAX){
			return result;
		}

		//2. exact matches for the typed word with an article (a/an/the/some), max 2 images each
		const articleRes=this.findImagesArticleExact(searchText,seenCodes,IMAGE_MATCH_MAX-result.length);
		for(const pair of articleRes){
			result.push(pair);
		}
		if(result.length>=IMAGE_MATCH_MAX){
			return result;
		}

		//3. partial prefix matches fill the rest
		const partialRes=this.findImagesPartialPrefix(searchText,{
			excludeExact:true,
			seenCodes:seenCodes,
			maxImages:IMAGE_MATCH_MAX-result.length,
		});
		for(const pair of partialRes){
			result.push(pair);
		}
		return result;
    }

	private findImagesArticleExact(searchText:string,seenCodes:Set<string>,maxImages:number):string[][]{
		const res:string[][]=[];
		if(searchText.length===0||maxImages<=0){
			return res;
		}
		for(const determiner of DETERMINERS){
			const candidate=determiner+searchText;
			const indexFound=this.items.indexOf(candidate);
			if(indexFound===-1)continue;
			const keyCaps=this.itemsWithCaps[indexFound];
			const codes=IM_CODES[keyCaps];
			let addedFromKey=0;
			for(let i=0;i<codes.length;i++){
				const code=codes[i];
				if(seenCodes.has(code))continue;
				seenCodes.add(code);
				res.push([keyCaps,code]);
				addedFromKey++;
				if(res.length>=maxImages)return res;
				if(addedFromKey>=ARTICLE_EXACT_PER_KEY_MAX)break;
			}
		}
		return res;
	}

	private findImagesExact(searchText:string):string[][]{
		let searchRes:string[][]=[];
		let indexFound=this.items.indexOf(searchText);
		if(indexFound!==-1){
			let searchTextWithCaps=this.itemsWithCaps[indexFound];
			for(let i=0;i<IM_CODES[searchTextWithCaps].length;i++){
				searchRes.push([searchTextWithCaps,IM_CODES[searchTextWithCaps][i]])
			}

			//this.items can have duplicate entries when IM_CODES has one capitalized and one uncapitalized version of the same word
			//the duplicate is always immediated after 
			//this ensures that the ims for the duplicates are also added to searchRes
			if(indexFound+1<this.items.length&&this.items[indexFound+1]===searchText){
				let searchTextWithCaps=this.itemsWithCaps[indexFound+1];
				for(let i=0;i<IM_CODES[searchTextWithCaps].length;i++){
					searchRes.push([searchTextWithCaps,IM_CODES[searchTextWithCaps][i]])
				}
			}
		}
		return searchRes;
	}

	private findImagesPartialPrefix(searchText:string,options?:{excludeExact?:boolean; seenCodes?:Set<string>; maxImages?:number}):string[][]{
		if(searchText.length<PARTIAL_MATCH_MIN_LEN){
			return [];
		}
		const excludeExact=Boolean(options?.excludeExact);
		const maxImages=options?.maxImages??PARTIAL_MATCH_MAX_IMAGES;
		const indices:number[]=[];
		for(let i=0;i<this.items.length;i++){
			const item=this.items[i];
			if(!item.startsWith(searchText)){
				continue;
			}
			if(excludeExact&&item===searchText){
				continue;
			}
			indices.push(i);
		}
		indices.sort((a,b)=>{
			const usageDiff=(this.usageByItem[this.items[b]]||0)-(this.usageByItem[this.items[a]]||0);
			if(usageDiff!==0){
				return usageDiff;
			}
			const lenDiff=this.items[b].length-this.items[a].length;
			if(lenDiff!==0){
				return lenDiff;
			}
			return a-b;
		});

		const searchRes:string[][]=[];
		const seenCodes=options?.seenCodes??new Set<string>();
		type PartialWordMatch={
			keyCaps:string;
			codes:string[];
			nextCodeIndex:number;
		};
		const wordMatches:PartialWordMatch[]=[];
		for(const idx of indices){
			const keyCaps=this.itemsWithCaps[idx];
			const codes=IM_CODES[keyCaps];
			wordMatches.push({
				keyCaps,
				codes,
				nextCodeIndex:0,
			});
		}

		// Pass 1: preserve variety with up to N images per matched word.
		outer:for(const wordMatch of wordMatches){
			let addedFromKey=0;
			while(wordMatch.nextCodeIndex<wordMatch.codes.length){
				const code=wordMatch.codes[wordMatch.nextCodeIndex];
				wordMatch.nextCodeIndex++;
				if(seenCodes.has(code)){
					continue;
				}
				seenCodes.add(code);
				searchRes.push([wordMatch.keyCaps,code]);
				addedFromKey++;
				if(searchRes.length>=maxImages){
					break outer;
				}
				if(addedFromKey>=PARTIAL_MATCH_PER_KEY_MAX){
					break;
				}
			}
		}

		// Pass 2: if still under cap, append additional images from same matched words.
		if(searchRes.length<maxImages){
			outerSecond:for(const wordMatch of wordMatches){
				while(wordMatch.nextCodeIndex<wordMatch.codes.length){
					const code=wordMatch.codes[wordMatch.nextCodeIndex];
					wordMatch.nextCodeIndex++;
					if(seenCodes.has(code)){
						continue;
					}
					seenCodes.add(code);
					searchRes.push([wordMatch.keyCaps,code]);
					if(searchRes.length>=maxImages){
						break outerSecond;
					}
				}
			}
		}
		return searchRes;
	}
	
	findSimilarText(searchText:string){
		if(searchText.length===0){
			return [];
		}
		type SimilarTextCandidate={
			item:string;
			prefixMatchLen:number;
			usage:number;
			index:number;
		};
		const candidatesMap=new Map<string, SimilarTextCandidate>();
		for(let i=0;i<this.items.length;i++){
			const item=this.items[i];
			if(item===searchText){
				continue;
			}
			const prefixMatchLen=this.getPrefixMatchLen(item,searchText);
			if(prefixMatchLen===0){
				continue;
			}
			const usage=this.usageByItem[item]||0;
			const existing=candidatesMap.get(item);
			if(!existing){
				candidatesMap.set(item,{item,prefixMatchLen,usage,index:i});
				continue;
			}
			if(usage>existing.usage){
				existing.usage=usage;
			}
			if(i<existing.index){
				existing.index=i;
			}
		}
		const candidates=[...candidatesMap.values()];
		candidates.sort((a,b)=>{
			if(a.prefixMatchLen!==b.prefixMatchLen){
				return b.prefixMatchLen-a.prefixMatchLen;
			}
			if(a.usage!==b.usage){
				return b.usage-a.usage;
			}
			return a.index-b.index;
		});
		const matches:string[]=[];
		for(let i=0;i<candidates.length&&matches.length<SIMILAR_TEXT_MAX;i++){
			matches.push(candidates[i].item);
		}
		return matches;
	}

	private getPrefixMatchLen(item:string,searchText:string):number{
		const maxLen=Math.min(item.length,searchText.length);
		let i=0;
		while(i<maxLen&&item[i]===searchText[i]){
			i++;
		}
		return i;
	}
}
